import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function Hero({
  onViewEvents,
  onReserve
}: {
  onViewEvents: () => void;
  onReserve: () => void;
}) {
  return <section id="hero" className="relative w-full h-screen min-h-[700px] overflow-hidden flex items-center justify-center" data-id="element-270">
      {/* Video Background */}
      <div className="absolute inset-0 z-0" data-id="element-271">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-80" data-id="element-272">
          <source src="https://cdn.magicpatterns.com/uploads/8xXpMfMU9Y1gyzemWNkC31/Chase _Welcome To Saphire Lounge_ Ad.mp4" type="video/mp4" data-id="element-273" />
        </video>
      </div>

      {/* Animated gradient background (Overlay) */}
      <motion.div className="absolute inset-0 z-0 mix-blend-overlay" animate={{
      background: ['radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.15) 0%, rgba(5,5,5,0.4) 60%), radial-gradient(ellipse at 80% 20%, rgba(201,168,76,0.1) 0%, rgba(5,5,5,0.4) 50%)', 'radial-gradient(ellipse at 60% 30%, rgba(201,168,76,0.2) 0%, rgba(5,5,5,0.4) 60%), radial-gradient(ellipse at 20% 70%, rgba(201,168,76,0.12) 0%, rgba(5,5,5,0.4) 50%)', 'radial-gradient(ellipse at 40% 70%, rgba(201,168,76,0.18) 0%, rgba(5,5,5,0.4) 60%), radial-gradient(ellipse at 70% 40%, rgba(201,168,76,0.14) 0%, rgba(5,5,5,0.4) 50%)', 'radial-gradient(ellipse at 20% 50%, rgba(201,168,76,0.15) 0%, rgba(5,5,5,0.4) 60%), radial-gradient(ellipse at 80% 20%, rgba(201,168,76,0.1) 0%, rgba(5,5,5,0.4) 50%)']
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} style={{
      backgroundSize: '100% 100%'
    }} data-id="element-274" />

      {/* Particle-like gold shimmer dots */}
      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute rounded-full bg-[#C9A84C] z-0" style={{
      width: Math.random() * 3 + 1,
      height: Math.random() * 3 + 1,
      left: `${10 + i * 15}%`,
      top: `${20 + i % 3 * 25}%`
    }} animate={{
      opacity: [0, 0.6, 0],
      y: [0, -40, -80],
      x: [0, i % 2 === 0 ? 10 : -10]
    }} transition={{
      duration: 4 + i * 0.5,
      repeat: Infinity,
      delay: i * 0.8,
      ease: 'easeOut'
    }} data-id="element-275" />)}

      {/* Gradient overlays for text readability */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/20 via-transparent to-[#050505]/80" data-id="element-276" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-[#050505]/80 via-black/10 to-[#050505]/30" data-id="element-277" />

      {/* Center content */}
      <div className="relative z-10 text-center px-6 flex flex-col items-center" data-id="element-278">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3
      }} className="mb-3" data-id="element-279">
          <span className="font-montserrat text-xs tracking-[0.4em] text-[#C9A84C] font-600 uppercase" data-id="element-280">
            Washington DC
          </span>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0,
        scale: [1, 1.018, 1, 1.012, 1, 1]
      }} transition={{
        opacity: {
          duration: 0.9,
          delay: 0.5
        },
        y: {
          duration: 0.9,
          delay: 0.5
        },
        scale: {
          delay: 1.8,
          duration: 0.55,
          repeat: Infinity,
          repeatDelay: 2.2,
          times: [0, 0.15, 0.3, 0.45, 0.65, 1],
          ease: 'easeInOut'
        }
      }} className="font-montserrat font-black text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-widest text-[#C9A84C] leading-none mb-4" style={{
        textShadow: '0 0 40px rgba(201,168,76,0.9), 0 0 80px rgba(201,168,76,0.5), 0 0 120px rgba(201,168,76,0.3)'
      }} data-id="element-281">
          MK LOUNGE
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.7
      }} className="font-inter font-light text-[19px] sm:text-[22px] md:text-[24px] text-white/70 tracking-widest max-w-lg" data-id="element-282">
          Washington DC's Premier Nightlife Experience
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        scaleX: 0
      }} animate={{
        opacity: 1,
        scaleX: 1
      }} transition={{
        duration: 0.8,
        delay: 0.9
      }} className="w-24 h-px bg-[#C9A84C]/60 mt-6 mb-8" data-id="element-283" />

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1.1
      }} className="flex gap-4" data-id="element-284">
          <button onClick={onViewEvents} className="font-montserrat font-700 text-xs tracking-widest bg-[#C9A84C] text-[#050505] px-7 py-3 rounded hover:bg-[#E8C97A] transition-colors duration-300" data-id="element-285">
            VIEW EVENTS
          </button>
          <button onClick={onReserve} className="font-montserrat font-700 text-xs tracking-widest border border-[#C9A84C]/60 text-[#C9A84C] px-7 py-3 rounded hover:border-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all duration-300" data-id="element-286">
            RESERVE
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 0.6
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/30 z-10 hidden sm:flex" data-id="element-287">
        <span className="font-inter text-[10px] tracking-widest" data-id="element-288">SCROLL</span>
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-289">
          <ChevronDownIcon size={14} data-id="element-290" />
        </motion.div>
      </motion.div>
    </section>;
}